import * as React from "react";

import { Button } from "@replohq/lattice/components/button/Button";
import { toast } from "@replohq/lattice/components/toast/Toast";
import { cn } from "@replohq/lattice/utils/cn";
import { skipToken } from "@tanstack/react-query";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useAgentApplySubscriptionChangeMutation from "@v2/features/agent-billing/hooks/useAgentApplySubscriptionChangeMutation";
import {
  agentBillingRouter,
  agentBillingUtils,
} from "@v2/trpc/routes/agentBilling";
import { workspaceUtils } from "@v2/trpc/routes/workspace";

import { ALWAYS_FRESH, formatDate } from "./shared";

export const SubscriptionActions: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const [status, setStatus] = React.useState<string | null>(null);
  const { data: activeSubscription, isLoading } =
    agentBillingRouter.subscription.find.useQuery(
      workspaceId ? { workspaceId } : skipToken,
      ALWAYS_FRESH,
    );

  const { mutate: cancelSubscription, isPending: isCancelPending } =
    agentBillingRouter.checkout.cancelSubscription.useMutation({
      onSuccess: () => {
        void workspaceUtils.getUserWorkspacesList.invalidate();
        void agentBillingUtils.subscription.find.invalidate();
        void agentBillingUtils.subscription.findRateCards.invalidate();
        void agentBillingUtils.changelogEntries.find.invalidate();
        setStatus("Plan cancellation scheduled.");
      },
    });

  const { startSubscriptionChange, isLoading: isResumePending } =
    useAgentApplySubscriptionChangeMutation();

  const handleCancel = () => {
    if (!workspaceId || !activeSubscription || activeSubscription.cancelAt) {
      return;
    }
    cancelSubscription({ workspaceId });
  };

  const handleResume = () => {
    if (!workspaceId || !activeSubscription) {
      return;
    }

    startSubscriptionChange(
      {
        workspaceId,
        subscriptionView: {
          subscriptionItems: activeSubscription.items.map((item) => ({
            productType: item.productType,
            priceId: item.stripePriceId,
            quantity: item.quantity,
            interval: activeSubscription.interval,
          })),
        },
        successUrl: window.location.href,
        cancelUrl: window.location.href,
      },
      {
        onSuccess: () => {
          toast.success({ title: "Subscription resumed" });
          void agentBillingUtils.subscription.find.invalidate();
          void agentBillingUtils.changelogEntries.find.invalidate();
          setStatus(null);
        },
      },
    );
  };

  if (isLoading) {
    return <div className="text-muted py-3 text-xs">Loading...</div>;
  }

  if (!activeSubscription) {
    return (
      <div className="text-muted py-3 text-xs">
        No active subscription to cancel.
      </div>
    );
  }

  const isAlreadyCancelled = Boolean(activeSubscription.cancelAt);

  return (
    <div className="flex items-center gap-3">
      {isAlreadyCancelled ? (
        <Button
          variant="primary"
          size="sm"
          disabled={isResumePending}
          loading={isResumePending}
          onClick={handleResume}
        >
          Resume Plan
        </Button>
      ) : (
        <Button
          variant="destructive"
          size="sm"
          disabled={isCancelPending}
          loading={isCancelPending}
          onClick={handleCancel}
        >
          Cancel Plan
        </Button>
      )}
      <span className="text-muted text-xs">
        {isAlreadyCancelled && activeSubscription.cancelAt
          ? `Ends on ${formatDate(activeSubscription.cancelAt)}`
          : "Cancels at the end of the current billing period."}
      </span>
      {status && (
        <span
          className={cn(
            "text-xs",
            status.startsWith("Error") ? "text-rose" : "text-emerald",
          )}
        >
          {status}
        </span>
      )}
    </div>
  );
};
